/**
 * Creates a memoized lazy import helper.
 *
 * @template TModule The resolved module shape returned by `loader`.
 *
 * @param loader A function that performs the dynamic `import()` when invoked.
 * @returns A memoized function that triggers `loader` only once and reuses the same promise afterwards.
 *
 * @example
 * ```ts
 * // store/lazy.ts
 * import { createLazyImport } from 'shared/lib/createLazyImport';
 * export const getStoreModule = createLazyImport(() => import('store'));
 *
 * // consumer.ts
 * const store = await getStoreModule();
 * ```
 *
 * @description
 * ### How it works
 * - On the first call it executes `loader()` (usually a dynamic `import()`),
 *   saves the returned promise and returns it to the caller.
 * - Subsequent calls reuse the cached promise, so the underlying module is
 *   loaded exactly once even if multiple consumers call the getter.
 *
 * ### When to use
 * - To break circular dependencies between modules (e.g. api ↔ store).
 * - To delay loading heavy modules until they are really needed.
 * - To keep plain helpers decoupled from specific modules (KISS / DRY).
 *
 * ### Testing scenarios
 * - **Multiple callers**: invoke the getter twice and ensure `loader` runs
 *   only once (can be tested by spying on `loader`).
 * - **Error propagation**: if the promise rejects, the rejection bubble ups
 *   to the caller — next call will retry because the cached promise was never set.
 */
export const createLazyImport = <TModule>(loader: () => Promise<TModule>) => {
  let modulePromise: Promise<TModule> | undefined;

  return () => {
    if (!modulePromise) {
      modulePromise = loader();
    }

    return modulePromise;
  };
};
